import styles from './MessageInput.module.scss';
import classes from 'classnames';
import SVG from 'react-inlinesvg';
import speaker from '@/assets/svg/speaker1.svg';
import Send from '@/assets/svg/Send.svg';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import UseMelingoVoiceAndAvatarStt from '@/hooks/UseMelingoVoiceAndAvatarSttSdk';
import { useTranslation } from 'react-i18next';
import MicrophonePermissionsStyles from '@/components/microphone-permissions/MicrophonePermissionsInfo.module.scss';
import MicrophonePermissionsInfo, {
  MicrophoneErrorTypes,
} from '@/components/microphone-permissions/MicrophonePermissionsInfo';
import { Transition } from 'react-transition-group';
import AppModal from '@/components/AppModal';
import { TutorChatMessageInputType } from '@mavatar/melingo-voice-and-avatar-sdk';

interface MessageInputProps {
  onSend: (message: string, inputType: TutorChatMessageInputType) => void;
  isRecordingDisabled: boolean;
  disabled?: boolean;
}

const MessageInput = ({ onSend, isRecordingDisabled, disabled = false }: MessageInputProps) => {
  const {
    text,
    interimText,
    isListening,
    isStreaming,
    onToggle,
    onReset,
    microphoneError,
    permissionError,
    setPermissionError,
  } = UseMelingoVoiceAndAvatarStt(30000, false);
  const [userInput, setUserInput] = useState('');
  const [streamingView, setStreamingView] = useState(false);
  const { t } = useTranslation();
  const sttText = useRef<string>('');

  useEffect(() => {
    if (isStreaming) {
      const timer = setTimeout(() => {
        setStreamingView(true);
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }

    setStreamingView(false);
  }, [isStreaming]);

  const handleSend = useCallback(
    (text?: string) => {
      const textToSend = (text || userInput || '').trim();
      if (textToSend) {
        onSend(
          textToSend,
          sttText.current === textToSend
            ? TutorChatMessageInputType.STT
            : TutorChatMessageInputType.TEXT
        );
        sttText.current = '';
        onReset();
        setUserInput('');
      }
    },
    [onSend, onReset, userInput]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleSend();
      }
    },
    [handleSend]
  );

  useEffect(() => {
    if (isRecordingDisabled && (isListening || interimText || text)) {
      onReset();
      sttText.current = '';
    }

    if (isRecordingDisabled && isListening) {
      setUserInput('');
      sttText.current = '';
    }

    if (!isRecordingDisabled && isListening && (text || interimText)) {
      const newInputText = [text || '', interimText || ''].join(' ').trim();
      setUserInput(newInputText);
      sttText.current = newInputText;
    }

    if (!isRecordingDisabled && !isListening && text) {
      //setUserInput('');
      //handleSend(text);
      onReset();
    }
  }, [isRecordingDisabled, isListening, interimText, text]);

  const microphoneErrorPopup = (
    <Transition
      in={permissionError}
      timeout={400}
      mountOnEnter
      unmountOnExit
      children={state => {
        return (
          <AppModal
            className={classes(MicrophonePermissionsStyles.permissionOverlay, `fade-${state}`)}
          >
            <div className={MicrophonePermissionsStyles.permissionContent}>
              <MicrophonePermissionsInfo
                onClose={() => {
                  setPermissionError(false);
                }}
                fromQuiz={true}
                errorType={microphoneError || MicrophoneErrorTypes.device_not_found}
              />
            </div>
          </AppModal>
        );
      }}
    />
  );

  return (
    <>
      <div className={styles.messageInput}>
        <div className={styles.input}>
          <input
            type='text'
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('writeOrSay').toString()}
            disabled={disabled}
          />
          {/*<button onClick={onToggle} className={styles.sttBtn}>
            {isListening ? (
              <img src={Record} alt='Recording' />
            ) : (
              <SVG src={speaker} />
            )}
          </button>*/}
          <button
            onClick={onToggle}
            className={classes(styles.sttBtn, {
              [styles.recording]: isListening && streamingView,
              [styles.waiting]: isListening && !streamingView,
            })}
            disabled={isRecordingDisabled || disabled}
          >
            {!isListening && <SVG src={speaker} />}
            {isListening && <div className={styles.sttBtnStopIcon}></div>}
            <div className={styles.circle}></div>
            <div className={styles.circle}></div>
            <div className={styles.circle}></div>
          </button>
        </div>
        <button
          onClick={() => {
            handleSend();
          }}
          className={styles.sendBtn}
          disabled={isListening || disabled}
        >
          <SVG src={Send} />
        </button>
      </div>
      {microphoneErrorPopup}
    </>
  );
};

export default MessageInput;
